import { Think, ThoughtChain } from '@ant-design/x'
import type { ThoughtChainItemType } from '@ant-design/x'
import { Collapse, Typography } from 'antd'
import { Check, Wrench, X } from 'lucide-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { ApprovalCard } from './ApprovalCard'
import { ChatMarkdown } from './ChatMarkdown'
import { QuestionCard } from './QuestionCard'
import { ResolvedQuestionCard } from './ResolvedQuestionCard'
import { useStyles } from './styles'
import { ThinkingShimmer } from './ThinkingShimmer'
import {
  isHandoffTimelineItem,
  splitTimeline,
  splitTimelineStages,
  timelineStageAnswerSegments
} from './timeline'
import type { AnswerSegment, TimelineStage } from './timeline'
import type {
  ApprovalResolution,
  QuestionResolution,
  TimelineItem
} from './types'
import { humaniseSpeaker, humaniseTool } from './utils'

const { Text } = Typography

const STEP_ICON_SIZE = 13

type Props = {
  timeline: TimelineItem[]
  streaming: boolean
  live?: boolean
  onApprovalResolve?: (action: ApprovalResolution) => void
  onQuestionResolve?: (resolution: QuestionResolution) => void
}

type TimelineGroup =
  | { kind: 'reasoning'; key: string; text: string; speaker?: string }
  | {
      kind: 'steps'
      key: string
      steps: ThoughtChainItemType[]
      currentStep: string
    }

const SpeakerLabel = ({ speaker }: { speaker?: string }) => {
  const { styles } = useStyles()
  const label = humaniseSpeaker(speaker)

  if (!label) return null

  return (
    <Text className={styles.speakerLabel} type="secondary">
      {label}
    </Text>
  )
}

const AnswerSegmentView = ({
  segment,
  streaming
}: {
  segment: AnswerSegment
  streaming: boolean
}) => (
  <div>
    <SpeakerLabel speaker={segment.speaker} />
    <ChatMarkdown content={segment.text} streaming={streaming} />
  </div>
)

const TimelineAnswer = ({
  segments,
  streaming
}: {
  segments: AnswerSegment[]
  streaming: boolean
}) => (
  <>
    {segments.map((segment, index) => (
      <AnswerSegmentView
        key={index}
        segment={segment}
        streaming={streaming && index === segments.length - 1}
      />
    ))}
  </>
)

const TimelineThoughts = ({
  stage,
  streaming
}: {
  stage: TimelineStage
  streaming: boolean
}) => {
  const { styles } = useStyles()
  const { t } = useTranslation()
  const { working, final } = splitTimeline(stage.items)
  const hasAnswer = final.some(
    (item) => item.kind === 'text' && item.text.trim()
  )

  const groups: TimelineGroup[] = []
  const pendingSteps: Array<{
    step: ThoughtChainItemType
    name?: string
    id?: string
  }> = []
  let currentStepGroup: Extract<TimelineGroup, { kind: 'steps' }> | undefined

  const addStep = (step: ThoughtChainItemType, currentStep: string) => {
    if (!currentStepGroup) {
      currentStepGroup = {
        kind: 'steps',
        key: `steps-${groups.length}`,
        steps: [],
        currentStep
      }
      groups.push(currentStepGroup)
    }
    currentStepGroup.steps.push(step)
    currentStepGroup.currentStep = currentStep
  }

  const completeStep = (step: ThoughtChainItemType) => {
    step.status = 'success'
    step.icon = <Check size={STEP_ICON_SIZE} />
  }

  const findLoadingStep = (
    matches: (entry: (typeof pendingSteps)[number]) => boolean
  ) => {
    for (let i = pendingSteps.length - 1; i >= 0; i--) {
      const entry = pendingSteps[i]
      if (entry.step.status === 'loading' && matches(entry)) return entry
    }

    return undefined
  }

  const completePendingStep = (name?: string, id?: string) => {
    const matched =
      (id && findLoadingStep((entry) => entry.id === id)) ||
      (name && findLoadingStep((entry) => entry.name === name)) ||
      undefined
    if (matched) completeStep(matched.step)

    return matched
  }

  working.forEach((item, index) => {
    if (isHandoffTimelineItem(item)) return
    if (item.kind === 'reasoning') {
      const previousGroup = groups[groups.length - 1]
      if (
        previousGroup?.kind === 'reasoning' &&
        previousGroup.speaker === item.speaker
      ) {
        previousGroup.text += `\n\n${item.text}`
      } else {
        groups.push({
          kind: 'reasoning',
          key: `${index}`,
          text: item.text,
          speaker: item.speaker
        })
      }
      currentStepGroup = undefined
    } else if (item.kind === 'tool_call') {
      const carriesNothing = !item.name && !item.args
      if (carriesNothing) return
      const title = item.name
        ? humaniseTool(item.name)
        : t('workspaceHome.timeline.request')
      const isQuestionStep = item.name === 'ask_user' && Boolean(stage.question)
      const step: ThoughtChainItemType = {
        key: `${index}`,
        title,
        description: humaniseSpeaker(item.speaker),
        status: isQuestionStep ? 'success' : 'loading',
        icon: isQuestionStep ? (
          <Check size={STEP_ICON_SIZE} />
        ) : (
          <Wrench size={STEP_ICON_SIZE} />
        ),
        content: item.args ? (
          <pre className={styles.toolPayload}>{item.args}</pre>
        ) : undefined,
        collapsible: Boolean(item.args)
      }
      addStep(step, title)
      if (!isQuestionStep) {
        pendingSteps.push({ step, name: item.name, id: item.id })
      }
    } else if (item.kind === 'tool_result') {
      const matched = completePendingStep(item.name, item.id)
      if (matched) {
        const callWasUnnamed = !matched.name
        if (callWasUnnamed && item.name) {
          matched.step.title = humaniseTool(item.name)
        }
        if (item.output) {
          matched.step.content = (
            <pre className={styles.toolPayload}>{item.output}</pre>
          )
          matched.step.collapsible = true
        }
      } else if (item.name !== 'ask_user' && (item.name || item.output)) {
        const title = item.name
          ? humaniseTool(item.name)
          : t('workspaceHome.timeline.response')
        addStep(
          {
            key: `${index}`,
            title,
            status: 'success',
            icon: <Check size={STEP_ICON_SIZE} />,
            content: item.output ? (
              <pre className={styles.toolPayload}>{item.output}</pre>
            ) : undefined,
            collapsible: Boolean(item.output)
          },
          title
        )
      }
    } else if (item.kind === 'approval') {
      completePendingStep(item.tool)

      if (item.status !== 'resolved' || !item.resolution) return
      const accepted = item.resolution !== 'reject'
      const title = `${
        accepted
          ? t('workspaceHome.approval.approved')
          : t('workspaceHome.approval.rejected')
      } · ${humaniseTool(item.tool)}`
      addStep(
        {
          key: `${index}`,
          title,
          status: accepted ? 'success' : 'error',
          icon: accepted ? (
            <Check size={STEP_ICON_SIZE} />
          ) : (
            <X size={STEP_ICON_SIZE} />
          )
        },
        title
      )
    }
  })

  const loadingSteps = pendingSteps
    .map(({ step }) => step)
    .filter((step) => step.status === 'loading')
  const allSteps: ThoughtChainItemType[] = []
  groups.forEach((group) => {
    if (group.kind === 'steps') allSteps.push(...group.steps)
  })
  const activeStep = streaming
    ? (loadingSteps[loadingSteps.length - 1] ?? allSteps[allSteps.length - 1])
    : undefined
  loadingSteps.forEach((step) => {
    if (step !== activeStep) {
      completeStep(step)
    }
  })
  if (activeStep) activeStep.blink = true
  const activeStepGroupKey = activeStep
    ? groups.find(
        (group) => group.kind === 'steps' && group.steps.includes(activeStep)
      )?.key
    : undefined

  const hasWorking = groups.length > 0
  const thoughtsActive = streaming && !hasAnswer
  if (!hasWorking && !thoughtsActive) return null
  if (!hasWorking) return <ThinkingShimmer />

  return (
    <Think
      key={thoughtsActive ? 'thinking' : 'thoughts'}
      className={styles.thinking}
      title={
        thoughtsActive
          ? t('workspaceHome.timeline.thinking')
          : t('workspaceHome.timeline.thoughts')
      }
      blink={thoughtsActive}
      defaultExpanded={thoughtsActive}
    >
      {groups.map((group) =>
        group.kind === 'reasoning' ? (
          <div key={group.key} className={styles.reasoningBody}>
            <SpeakerLabel speaker={group.speaker} />
            <ChatMarkdown content={group.text} streaming={streaming} />
          </div>
        ) : (
          <Collapse
            key={group.key}
            ghost
            size="small"
            className={
              group.key === activeStepGroupKey
                ? `${styles.stepGroup} ${styles.stepGroupBlink}`
                : styles.stepGroup
            }
            items={[
              {
                key: group.key,
                showArrow: false,
                collapsible: 'header',
                label: `${t('workspaceHome.timeline.working')}: ${
                  group.currentStep
                } · ${group.steps.length}`,
                children: <ThoughtChain items={group.steps} />
              }
            ]}
          />
        )
      )}
    </Think>
  )
}

const AssistantTimelineBase = ({
  timeline,
  streaming,
  live,
  onApprovalResolve,
  onQuestionResolve
}: Props) => {
  const { styles } = useStyles()
  const stages = splitTimelineStages(timeline)
  const currentStage = stages[stages.length - 1]
  const previousStages = stages.slice(0, -1)
  const { working } = splitTimeline(currentStage.items)
  const answerSegments = timelineStageAnswerSegments(currentStage.items)
  const hasAnswer = answerSegments.some((segment) => segment.text.trim())

  const pendingApproval = working.find(
    (item) => item.kind === 'approval' && item.status === 'pending'
  )
  const pendingQuestion =
    currentStage.question?.status === 'pending'
      ? currentStage.question
      : undefined

  return (
    <div className={styles.timeline}>
      {previousStages.map((stage, index) => {
        const previousSegments = timelineStageAnswerSegments(stage.items)
        const resolvedQuestion = stage.question
        const resolution =
          resolvedQuestion?.status === 'resolved'
            ? resolvedQuestion.resolution
            : undefined

        return (
          <div key={`stage-${index}`}>
            <TimelineThoughts stage={stage} streaming={false} />
            <TimelineAnswer segments={previousSegments} streaming={false} />
            {resolvedQuestion && resolution ? (
              <ResolvedQuestionCard
                question={resolvedQuestion.question}
                resolution={resolution}
              />
            ) : null}
          </div>
        )
      })}

      <TimelineThoughts stage={currentStage} streaming={streaming} />

      {pendingApproval?.kind === 'approval' && onApprovalResolve ? (
        <ApprovalCard
          tool={pendingApproval.tool}
          value={pendingApproval.value}
          disabled={streaming}
          enter={live}
          onResolve={onApprovalResolve}
        />
      ) : null}

      {hasAnswer ? (
        <TimelineAnswer segments={answerSegments} streaming={streaming} />
      ) : null}

      {pendingQuestion?.kind === 'question' && onQuestionResolve ? (
        <QuestionCard
          key={pendingQuestion.approvalId}
          question={pendingQuestion.question}
          options={pendingQuestion.options}
          allowMultiple={pendingQuestion.allowMultiple}
          disabled={streaming}
          enter={live}
          onResolve={onQuestionResolve}
        />
      ) : null}
    </div>
  )
}

export const AssistantTimeline = memo(AssistantTimelineBase)
